<template>
  <!-- Mobile Menu Trigger -->
  <button
    class="cursor-pointer block xl:hidden mt-1 text-mbzuai-sand-500 hover:decoration-mbzuai-sand-200 w-[27px] h-[27px] border-0 p-0 m-0"
    aria-label="Open menu"
    @click="open"
  >
    <svg
      width="27"
      height="22"
      viewBox="0 0 27 22"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      <g clip-path="url(#clip0_22415_48322)">
        <path
          d="M4.8113 4.12694L2.1171 4.13212L0.000817707 2.01583L2.12624 -0.111543L4.82044 -0.116726L24.8837 -0.134473L27 1.98181L24.8746 4.10919L4.8113 4.12694Z"
          fill="#E5C687"
        />
        <path
          d="M4.8113 13.1282L2.1171 13.1333L0.000817707 11.0171L2.12624 8.88968L4.82044 8.88449L24.8837 8.86675L27 10.983L24.8746 13.1104L4.8113 13.1282Z"
          fill="#E5C687"
        />
        <path
          d="M4.8113 22.1293L2.1171 22.1344L0.000817707 20.0182L2.12624 17.8908L4.82044 17.8856L24.8837 17.8678L27 19.9841L24.8746 22.1115L4.8113 22.1293Z"
          fill="#E5C687"
        />
      </g>
    </svg>
  </button>

  <!-- Mobile Drawer -->
  <Drawer
    v-model:visible="visible"
    class="w-full xl:hidden"
    :show-close-icon="false"
    @after-hide="resetStack"
  >
    <template #container="{ closeCallback }">
      <main class="flex flex-col h-full max-h-dvh">
        <header
          class="flex items-start gap-3 justify-between w-full bg-mbzuai-sand-500 p-3"
        >
          <!-- Back button only appears once drilled in -->
          <button
            v-if="currentParent"
            class="flex items-center gap-1 font-medium text-body-large-medium text-mbzuai-navy-500! cursor-pointer border-0 bg-transparent p-2"
            aria-label="Go back"
            @click="goBack"
          >
            <ChevronIcon direction="left" class="w-3!" />
            <span class="truncate max-w-[180px]">{{ parentBackLabel }}</span>
          </button>

          <!-- <SiteLanguageSwitcher v-else-if="useNuxtApp().$i18n" /> -->
          <div class="min-h-[43px]"></div>

          <MBZCTAButton
            label=""
            icon="times"
            severity="secondary"
            class="h-[30px] w-auto aspect-square p-[7px]!"
            @click.stop="closeCallback()"
          />
        </header>

        <section
          class="relative px-4 h-full overflow-hidden flex-1"
          aria-label="Mobile navigation"
        >
          <Transition :name="transitionName">
            <ul
              :key="depth"
              class="absolute inset-0 p-0 px-4 pt-6 overflow-y-auto list-none flex flex-col gap-5 m-0"
              :class="depth >= 2 && 'bg-mbzuai-sand-100'"
            >
              <!-- Link to the parent landing page when drilled into a level -->
              <li
                v-if="currentParent && depth === 1"
                class="flex flex-col gap-4 pb-4 border-b border-b-mbzuai-navy-500"
              >
                <MBZCTADefault
                  ref="focusMobileMenu"
                  tabindex="0"
                  class="text-h4"
                  :label="currentParent.title"
                  :url="{
                    title: currentParent.title,
                    href: currentParent.relative,
                    external: currentParent.external,
                  }"
                  @click="close"
                />
                <!-- Description -->
                <p
                  v-if="currentParent?.field_description?.length"
                  class="text-body-small"
                >
                  <DrupalMarkup
                    :content="
                      currentParent.field_description.map(
                        (description) => description.value
                      )
                    "
                  />
                </p>
              </li>

              <li v-for="item in currentItems" :key="item.title">
                <button
                  v-if="hasChildren(item)"
                  type="button"
                  class="mobile-submenu-trigger"
                  :class="depth === 0 ? 'text-h4' : 'text-body-large-medium'"
                  aria-haspopup="true"
                  @click="drillInto(item)"
                >
                  <span>{{ item.title }}</span>
                  <ChevronIcon
                    direction="right"
                    class="w-3! min-w-3! h-auto!"
                  />
                </button>

                <MBZCTADefault
                  v-else
                  :label="item.title"
                  :url="{
                    title: item.title,
                    href: item.relative,
                    external: item.external,
                  }"
                  :icon="null"
                  :class="depth === 0 ? 'text-h4' : 'text-body-large-medium'"
                  @click="close"
                />
              </li>

              <li
                v-if="depth > 0 && currentParent?.field_related_links"
                class="-mx-4 mt-auto px-4 flex flex-col gap-4 py-5 bg-mbzuai-navy-500"
              >
                <!-- Related links -->
                <p class="text-body-small-medium text-surface-0!">
                  {{ $t('Related Links') }}:
                </p>

                <ul class="flex flex-row flex-wrap gap-4">
                  <li
                    v-for="link in currentParent?.field_related_links || []"
                    :key="link.title"
                    class="link text-body-small"
                  >
                    <ParagraphCallToActionDefault
                      button-style="default"
                      :button-label="link.title"
                      :button-url="{
                        title: link.title,
                        href: link.uri,
                        external: link.options.external,
                      }"
                    />
                  </li>
                </ul>
              </li>

              <li
                v-if="depth === 0"
                class="border-t border-mbzuai-navy-500! py-6 w-full flex flex-col gap-6"
              >
                <Select
                  v-model="selectedOption"
                  :options="options"
                  option-label="name"
                  :placeholder="$t('Information for')"
                  class="max-w-[200px] shadow-none!"
                  :pt="{
                    root: {
                      class: 'border-none',
                    },
                    label: {
                      class: 'text-body-small-medium text-mbzuai-navy-500!',
                    },
                    dropdownIcon: {
                      class: 'hidden',
                    },
                  }"
                >
                  <template #dropdownicon>
                    <ChevronIcon
                      direction="down"
                      class="order-1 w-2! h-auto!"
                      severity="light"
                    />
                  </template>
                </Select>
              </li>

              <li
                v-if="depth === 0"
                class="-mx-4 mt-auto px-4 flex flex-col gap-4 py-5 pb-10 bg-white"
                style="box-shadow: 0 -10px 10px rgba(0, 0, 0, 0.05)"
              >
                <PanelMenu
                  :model="items"
                  :pt="{
                    root: {
                      class: 'border-0!',
                    },
                    panel: {
                      class:
                        'p-0 borer-0! border-transparent! bg-mbzuai-sand-50 rounded-[28px] w-full',
                    },
                    item: {
                      class:
                        'my-4 flex gap-3 w-full text-center text-mbzuai-navy-500! hover:bg-transprent!',
                    },
                    itemContent: {
                      class:
                        'hover:bg-transparent! hover:cursor-pointer! text-mbzuai-dark-navy-500! text-body-small!',
                    },
                  }"
                >
                  <template #item="item">
                    <template v-if="item.root">
                      <ParagraphCallToActionDefault
                        class="w-full max-w-full justify-center!"
                        button-style="secondary"
                        button-label="Login"
                        :icon="item.active ? 'chevronUp' : 'chevronDown'"
                      />
                    </template>

                    <template v-if="!item.root">
                      <p>
                        <NuxtLink
                          :to="item.item.url"
                          class="link text-mbzuai-dark-navy-500! no-underline!"
                        >
                          {{ item.item.label }}
                        </NuxtLink>
                      </p>
                    </template>
                  </template>
                </PanelMenu>

                <ParagraphCallToActionDefault
                  class="max-w-full md:text-center justify-center!"
                  button-style="primary"
                  button-label="Apply"
                />
              </li>
            </ul>
          </Transition>
        </section>
      </main>
    </template>
  </Drawer>
</template>

<script setup lang="ts">
import type { MenuItem } from '~/composables/useDrupalMenu';
import type { MenuItem as PrimeVueMenuItem } from 'primevue/menuitem';

const props = defineProps<{
  mainMenu: MenuItem[] | null;
}>();

const menuFocus = useTemplateRef('focusMobileMenu');
const visible = ref(false);

const selectedOption = ref();
const options = ref([]);

const items = ref<PrimeVueMenuItem[]>([
  {
    label: 'Login',
    items: [
      {
        label: 'Student Login',
        url: 'https://mbzuaiac.sharepoint.com/Student/Pages/default.aspx',
      },
      {
        label: 'Staff Login',
        url: 'https://mbzuaiac.sharepoint.com/Staff/Pages/default.aspx',
      },
    ],
  },
]);

// Drill-down navigation stack: each entry is the parent we drilled into.
// Empty stack = showing the top-level menu.
const stack = ref<MenuItem[]>([]);

// Controls the slide direction of the panel transition.
const transitionName = ref<'slide-forward' | 'slide-back'>('slide-forward');

const currentParent = computed<MenuItem | null>(
  () => stack.value.at(-1) ?? null
);

function getChildren(item: MenuItem | null): MenuItem[] {
  if (!item) return [];

  if (item.level_2?.length) return item.level_2;
  if (item.level_3?.length) return item.level_3;

  return [];
}

function hasChildren(item: MenuItem): boolean {
  return getChildren(item).length > 0;
}

const currentItems = computed<MenuItem[]>(() =>
  currentParent.value
    ? getChildren(currentParent.value)
    : (props.mainMenu ?? [])
);

// Used as the Transition key so a level change swaps panels.
const depth = computed(() => stack.value.length);

// Trim a long parent title for the back button without losing meaning.
const parentBackLabel = computed(() => {
  return depth.value >= 2 ? currentParent.value?.title : 'Back';
});

function open() {
  visible.value = true;
}

function close() {
  visible.value = false;
}

function drillInto(item: MenuItem) {
  if (!hasChildren(item)) return;

  transitionName.value = 'slide-forward';
  stack.value = [...stack.value, item];
}

function goBack() {
  transitionName.value = 'slide-back';
  stack.value = stack.value.slice(0, -1);
}

// Reset to the top level only after the drawer has fully closed so the
// user never sees the panel jump back during the close animation.
function resetStack() {
  stack.value = [];
  transitionName.value = 'slide-forward';
}

onMounted(() => {
  menuFocus.value?.focus();
});
</script>

<style scoped>
.mobile-submenu-trigger {
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 0;
  border: 0;
  background: transparent;
  color: var(--p-brand-navy-500);
  text-align: start;
  cursor: pointer;
}

.mobile-submenu-trigger:focus-visible {
  outline: 3px solid var(--p-brand-dark-navy-500);
  outline-offset: 4px;
}

/* Forward (drill in): new panel slides in from the right */
.slide-forward-enter-active,
.slide-forward-leave-active,
.slide-back-enter-active,
.slide-back-leave-active {
  transition:
    transform 0.3s ease,
    opacity 0.3s ease;
}

.slide-forward-enter-from {
  transform: translateX(100%);
  opacity: 0;
}
.slide-forward-leave-to {
  transform: translateX(-100%);
  opacity: 0;
}

/* Back: new panel slides in from the left */
.slide-back-enter-from {
  transform: translateX(-100%);
  opacity: 0;
}
.slide-back-leave-to {
  transform: translateX(100%);
  opacity: 0;
}
</style>
